import React from 'react';
import { motion } from 'framer-motion';
import { Target, Heart, Shield, Users } from 'lucide-react';

export default function About() {
  return (
    <div className="bg-white">
      {/* Cinematic Header */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <img 
          src="/images/about1.jpg" 
          alt="Giving Hands Mission" 
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gh-blue/80 backdrop-blur-sm"></div>
        <div className="relative z-10 text-center px-6">
          <h1 className="text-5xl md:text-7xl font-black text-white italic uppercase tracking-tighter">
            Our <span className="text-gh-orange">Story</span>
          </h1>
          <div className="w-24 h-2 bg-gh-orange mx-auto mt-4"></div>
        </div>
      </section>

      {/* Main Mission & Vision Section */}
      <section className="py-20 px-6 max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-gh-blue text-3xl font-black uppercase mb-6 flex items-center gap-3">
              <Target className="text-gh-orange" /> Our Vision
            </h2>
            <p className="text-gray-600 text-xl leading-relaxed italic border-l-4 border-gh-orange pl-6 mb-12">
              "A world where every child, regardless of ability, is embraced with love and given the tools to thrive."
            </p>

            <h2 className="text-gh-blue text-3xl font-black uppercase mb-6 flex items-center gap-3">
              <Heart className="text-gh-orange" /> Our Mission
            </h2>
            <p className="text-gray-700 text-lg leading-relaxed">
              To provide a loving home, basic needs, and specialized support to orphaned and abandoned children in Mityana, 
              empowering them with hope, purpose, and a better future through Christ-centered care.
            </p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative"
          >
            <img 
              src="/images/about2.jpg" 
              alt="Children playing" 
              className="rounded-[3rem] shadow-2xl w-full aspect-[4/5] object-cover"
            />

          </motion.div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="py-20 bg-slate-50 px-6">
        <div className="max-w-7xl mx-auto text-center mb-16">
          <h2 className="text-gh-blue text-4xl font-black uppercase italic">Why We Do This</h2>
          <p className="text-gray-500 mt-4">Our foundation is built on four unbreakable pillars.</p>
        </div>

        <div className="max-w-7xl mx-auto grid md:grid-cols-4 gap-8">
          {[
            { icon: <Heart size={30} />, title: "Unconditional Love", desc: "Every child is treated as a precious gift of God." },
            { icon: <Shield size={30} />, title: "Dignity & Care", desc: "We provide quality healthcare and safe shelter." },
            { icon: <Users size={30} />, title: "Community", desc: "Building a family environment for the lonely." },
            { icon: <Target size={30} />, title: "Hope", desc: "Empowering children to dream of a brighter future." }
          ].map((value, i) => (
            <div key={i} className="bg-white p-8 rounded-3xl shadow-sm hover:shadow-xl transition-shadow border-b-4 border-gh-orange">
              <div className="text-gh-orange mb-4">{value.icon}</div>
              <h4 className="text-gh-blue font-bold uppercase mb-2">{value.title}</h4>
              <p className="text-gray-500 text-sm leading-relaxed">{value.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Final Call to Action Photo */}
      <section className="py-20 px-6 max-w-7xl mx-auto">
        <div className="rounded-[4rem] overflow-hidden relative h-[500px]">
          <img 
            src="/images/about3.jpg" 
            alt="Community Outreach" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gh-blue via-gh-blue/40 to-transparent flex flex-col justify-end p-12">
            <h3 className="text-white text-4xl font-black uppercase mb-4 italic">Together, we can change lives.</h3>
            <p className="text-blue-50 text-lg max-w-2xl mb-8 font-medium">
              Join us in our journey to bring light into the darkest corners of poverty. 
              Your presence in our story makes all the difference.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}